/**
 * Firebase Service - Shared service for Firestore CRUD operations
 */

import {
    db, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc
} from '../../firebase-config.js';
import { toastError } from '../ui/toast.js';
import { normalizeApartmentRecord, serializeApartmentRecord } from '../utils/apartment-schema.js';

export interface FirebaseDocument {
    id?: string;
    [key: string]: any;
}

interface LoadCollectionOptions {
    silent?: boolean;
}

function stripUndefinedFields(value: any): any {
    if (Array.isArray(value)) {
        return value.map(item => stripUndefinedFields(item));
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .filter(([, entryValue]) => entryValue !== undefined)
                .map(([key, entryValue]) => [key, stripUndefinedFields(entryValue)])
        );
    }

    return value;
}

function normalizeDocument<T extends FirebaseDocument>(colName: string, payload: any, docId: string): T {
    if (colName === 'apartments') {
        return normalizeApartmentRecord({ ...payload, id: payload.id || docId }, docId) as T;
    }

    return { id: payload.id || docId, _docId: docId, ...payload } as T;
}

function prepareDocumentForSave(colName: string, payload: any, id?: string): any {
    if (colName === 'apartments') {
        return stripUndefinedFields(serializeApartmentRecord({ ...payload, id: payload.id || id }));
    }

    return stripUndefinedFields(payload);
}

/**
 * Firebase CRUD Service
 */
export const FirebaseService = {
    /**
     * Load all documents from a collection
     */
    async loadCollection<T extends FirebaseDocument>(colName: string, options: LoadCollectionOptions = {}): Promise<T[]> {
        try {
            const querySnapshot = await getDocs(collection(db, colName));
            if (querySnapshot.empty) return [];
            return querySnapshot.docs.map(doc => normalizeDocument<T>(colName, doc.data(), doc.id));
        } catch (e) {
            if (!options.silent) {
                console.error(`Error loading ${colName}:`, e);
            }
            throw e;
        }
    },

    /**
     * Get a single document by ID
     */
    async getDocument<T extends FirebaseDocument>(colName: string, id: string): Promise<T | null> {
        try {
            const docSnap = await getDoc(doc(db, colName, id));
            if (docSnap.exists()) {
                return normalizeDocument<T>(colName, docSnap.data(), docSnap.id);
            }
            return null;
        } catch (e) {
            console.error(`Error getting ${colName}/${id}:`, e);
            throw e;
        }
    },

    /**
     * Save a document (create or update with merge)
     */
    async save(colName: string, id: string, data: any): Promise<void> {
        try {
            await setDoc(doc(db, colName, id), prepareDocumentForSave(colName, data, id), { merge: true });
        } catch (e) {
            console.error(`Error saving to ${colName}:`, e);
            toastError('Kaydetme hatası! Lütfen internet bağlantınızı kontrol edip tekrar deneyin.');
            throw e;
        }
    },

    /**
     * Add a new document
     */
    async add(colName: string, data: any): Promise<string> {
        try {
            const normalizedData = prepareDocumentForSave(colName, data);
            // Use setDoc if id is provided, else addDoc
            if (normalizedData.id) {
                await setDoc(doc(db, colName, normalizedData.id), normalizedData);
                return normalizedData.id;
            } else {
                const docRef = await addDoc(collection(db, colName), normalizedData);
                return docRef.id;
            }
        } catch (e) {
            console.error(`Error adding to ${colName}:`, e);
            throw e;
        }
    },

    /**
     * Update an existing document
     */
    async update(colName: string, id: string, data: Partial<any>): Promise<void> {
        try {
            await updateDoc(doc(db, colName, id), prepareDocumentForSave(colName, data, id));
        } catch (e) {
            console.error(`Error updating ${colName}/${id}:`, e);
            toastError('Güncelleme hatası! Lütfen sayfayı yenileyip tekrar deneyin.');
            throw e;
        }
    },

    /**
     * Delete a document
     */
    async delete(colName: string, id: string): Promise<void> {
        try {
            await deleteDoc(doc(db, colName, id));
        } catch (e) {
            console.error(`Error deleting from ${colName}:`, e);
            toastError('Silme hatası! Lütfen sayfayı yenileyip tekrar deneyin.');
            throw e;
        }
    }
};

// Export as default for simpler imports
export default FirebaseService;
